import { delay, put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { IAdminCatalogForUpdate } from 'APIPackage/AdminModule/interfaces/IAdminCatalogForUpdate';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { fetchSettings, updateSetting } from 'APIPackage/AdminModule/services/adminCatalogsExportsApi';
import { fetchCatalogsForUpdate, updateCatalog } from 'APIPackage/AdminModule/services/adminCatalogsUpdateApi';
import { createKnmForm, updateKnmForm } from 'APIPackage/AdminModule/services/adminKnmFormsApi';
import { createKnmNoticeMethod, updateKnmNoticeMethod } from 'APIPackage/AdminModule/services/adminKnmNoticeMethodsApi';
import { createKnmSubjectType, updateKnmSubjectType } from 'APIPackage/AdminModule/services/adminKnmSubjectTypesApi';
import { createKnmType, updateKnmType } from 'APIPackage/AdminModule/services/adminKnmTypesApi';
import {
  createKnmViolationType,
  updateKnmViolationType,
} from 'APIPackage/AdminModule/services/adminKnmViolationTypesApi';
import { updateFrguSync } from 'APIPackage/AdminModule/services/adminOrganizationsAndFunctionsApi';
import { fetchSettingsSchedule, updateSettingSchedule } from 'APIPackage/AdminModule/services/adminSettingsScheduleApi';
import { fetchUserAdminUrl } from 'APIPackage/AdminModule/services/adminUsersApi';
import { createKnmAddressType, updateKnmAddressType } from 'APIPackage/CatalogsModule/services/knmAddressTypesApi';
import { createKnmDelegateType, updateKnmDelegateType } from 'APIPackage/CatalogsModule/services/knmDelegateTypesApi';
import {
  createKnmInspectorType,
  updateKnmInspectorType,
} from 'APIPackage/CatalogsModule/services/knmInspectorTypesApi';
import { createRiskCategory, updateRiskCategory } from 'APIPackage/CatalogsModule/services/riskCategoriesApi';
import {
  createTestPurchaseMethod,
  updateTestPurchaseMethod,
} from 'APIPackage/CatalogsModule/services/testPurchaseMethodsApi';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { INewsResponse } from 'APIPackage/NewsModule/interfaces/INewsResponse';
import { fetchNews } from 'APIPackage/NewsModule/services/newsApi';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { formatDate, parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_MINUTE_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { ISystemInoperability } from 'APIPackage/SystemInoperabilityModule/interfaces/ISystemInoperability';
import { ISystemInoperabilityResponse } from 'APIPackage/SystemInoperabilityModule/interfaces/ISystemInoperabilityResponse';
import {
  addSystemInoperabilityRecord,
  fetchSystemInoperabilityList,
} from 'APIPackage/SystemInoperabilityModule/services/systemInoperabilityApi';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { INTERNAL_SERVER_ERROR, SYSTEM_INOPERABILITY_NOT_ALL_SAVED } from 'CorePackage/MainModule/constants/httpErrors';
import {
  ADMIN_CATALOG_UPDATED_SUCCESS,
  SYSTEM_INOPERABILITY_ERROR_MESSAGE,
} from 'CorePackage/MainModule/constants/notices';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import {
  fetchManageTestUsersApiList,
  fetchUpdateManageTestUsersApiList,
} from 'APIPackage/ManageTestUserModule/services/manageTestUsersApi';
import { IManageTestUsersResponse } from 'APIPackage/ManageTestUserModule/interface/IManageTestUsersResponse';

import { updateKnmKind } from '../../../APIPackage/AdminModule/services/adminKnmKindsApi';
import {
  adminCatalogsExportsSubmit,
  adminCreateCatalogTypes,
  adminGetCatalogTypes,
  adminGetFetchSettings,
  adminGetFetchSettingsSuccess,
  adminGetSmevResendBySendingDate,
  adminGetSmevResendBySendingDateNextPage,
  adminSetCatalogName,
  adminSetCatalogsForUpdate,
  adminSetFetchSettings,
  adminSetNews,
  adminSetSmevResendBySendingDateList,
  adminSetSystemInoperabilityAddRecord,
  adminSetSystemInoperabilityFormUnsavedRecords,
  adminSetSystemInoperabilityList,
  adminSetUserAdminUrl,
  adminUpdateCatalogTypes,
  adminUpdateSettings,
  adminUpdateSettingsSchedule,
  adminUploadCatalogs,
  adminSetManageTestUsersList,
  adminGetManageTestUsersList,
  adminGetManageTestUsersListNextPage,
  adminUpdateManageTestUsersList,
} from '../actions/adminAction';
import { AdminActionsEnum } from '../enums/AdminActionsEnum';
import {
  getAdminControlOfNewslettersList,
  getAdminControlOfNewslettersListPage,
  getAdminMangeTestUsersList,
  getAdminMangeTestUsersListOrder,
  getAdminMangeTestUsersListPage,
  getAdminFetchSettings,
  getAdminNews,
  getAdminNewsOrder,
  getAdminNewsPage,
  getAdminSystemInoperabilityList,
  getAdminSystemInoperabilityListPage,
  getAdminSystemInoperabilityOrder,
} from '../selectors/adminSelectors';
import { coreSetLoading } from '../../../CorePackage/MainModule/actions/coreActions';
import {
  createKnmReasonCanceled,
  updateKnmReasonsCanceled,
} from '../../../APIPackage/CatalogsModule/services/knmReasonsCanceledApi';
import { fetchSmevResendBySendingDateList } from '../../../APIPackage/ControlIfNewsletterModule/services/controlIfNewsletterModuleApi';
import { ISmevResendResponse } from '../../../APIPackage/ControlIfNewsletterModule/interfaces/ISmevResendResponse';
import { createKnmRemark, updateKnmRemark } from '../../../APIPackage/CatalogsModule/services/knmRemarkApi';

const GET_CATALOGS_DELAY = 1500;

export function* watchAdminSaga() {
  yield takeEvery(AdminActionsEnum.ADMIN_GET_USER_ADMIN_URL, adminGetUserAdminUrlSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_GET_FETCH_SETTINGS, adminGetFetchSettingsSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_UPDATE_SETTINGS, adminUpdateSettingsSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_UPDATE_SETTINGS_SCHEDULE, adminUpdateSettingsScheduleSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_GET_CATALOGS_FOR_UPDATE, adminGetCatalogsForUpdateSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_UPLOAD_CATALOGS, adminUploadCatalogsSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_GET_CATALOG_TYPES, adminGetCatalogsTypesSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_CREATE_CATALOG_TYPES, adminCreateCatalogTypesSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_UPDATE_CATALOG_TYPES, adminUpdateCatalogTypesSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_UPDATE_FRGU, adminUpdateFrguSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_GET_NEWS, adminGetNewsSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_GET_NEWS_NEXT_PAGE, adminGetNewsNextPageSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_CATALOGS_EXPORTS_SUBMIT, adminCatalogsExportsSubmitSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_GET_SISTEM_INOPERABILITY_LIST, adminGetSystemInoperabilityListSaga);
  yield takeEvery(
    AdminActionsEnum.ADMIN_GET_SISTEM_INOPERABILITY_LIST_NEXT_PAGE,
    adminGetSystemInoperabilityListNextPageSaga,
  );
  yield takeEvery(AdminActionsEnum.ADMIN_SET_SISTEM_INOPERABILITY_LIST_ADD_RECORD, adminSetSystemInoperabilityRecord);
  yield takeEvery(AdminActionsEnum.ADMIN_GET_SMEV_RESEND_BY_SENDING_DATE, adminGetSmevResendBySendingDateListSaga);
  yield takeEvery(
    AdminActionsEnum.ADMIN_GET_SMEV_RESEND_BY_SENDING_DATE_NEXT_PAGE,
    adminGetSmevResendBySendingDateNextPageSaga,
  );
  yield takeEvery(AdminActionsEnum.ADMIN_GET_MANAGE_TEST_USERS_LIST, adminGetManageTestUsersListSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_GET_MANAGE_TEST_USERS_LIST_NEXT_PAGE, adminGetManageTestUsersListNextPageSaga);
  yield takeEvery(AdminActionsEnum.ADMIN_UPDATE_MANAGE_TEST_USERS_LIST, adminUpdateManageTestUsersListSaga);
}

function* adminGetUserAdminUrlSaga() {
  function* funcTry() {
    const res: string = yield fetchUserAdminUrl();

    yield put(adminSetUserAdminUrl(res));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* adminGetFetchSettingsSaga(action: ActionType<typeof adminGetFetchSettings>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const existingSettings = getAdminFetchSettings(state);

    if (!action.payload.force && existingSettings.length) {
      return;
    }

    const response: IAdminSetting[] = yield fetchSettings();
    const responseSchedule: IAdminSetting[] = yield fetchSettingsSchedule();

    yield put(adminSetFetchSettings([...response, ...responseSchedule]));
    yield put(adminGetFetchSettingsSuccess([...response, ...responseSchedule]));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* adminUpdateSettingsSaga(action: ActionType<typeof adminUpdateSettings>) {
  function* funcTry() {
    const { id, body } = action.payload;

    yield updateSetting(id, body);
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* adminUpdateSettingsScheduleSaga(action: ActionType<typeof adminUpdateSettingsSchedule>) {
  function* funcTry() {
    const { body } = action.payload;

    yield updateSettingSchedule(body);
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* adminGetCatalogsForUpdateSaga() {
  function* funcTry() {
    const response: IAdminCatalogForUpdate[] = yield fetchCatalogsForUpdate();

    yield put(adminSetCatalogsForUpdate(response));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* adminUploadCatalogsSaga(action: ActionType<typeof adminUploadCatalogs>) {
  yield sagaWrapper({
    *funcTry() {
      const { id, file } = action.payload;

      yield updateCatalog(id, file);

      yield put(
        addNoticeAction({
          renderContent: ADMIN_CATALOG_UPDATED_SUCCESS,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.UPDATE_ADMIN_CATALOG,
        }),
      );
    },
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.INTERNAL_SERVER,
        }),
      );
    },
  });
}

function* adminGetCatalogsTypesSaga(action: ActionType<typeof adminGetCatalogTypes>) {
  yield sagaWrapper({
    *funcTry() {
      const { catalogName } = action.payload;

      yield put(adminSetCatalogName(catalogName));
      yield put(catalogGet(catalogName));
    },
  });
}

function* adminCreateCatalogTypesSaga(action: ActionType<typeof adminCreateCatalogTypes>) {
  yield sagaWrapper({
    *funcTry() {
      switch (action.payload.params.catalogName) {
        case CommonCatalogsNamesEnum.KNM_ADDRESS_TYPES: {
          yield createKnmAddressType(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_REASONS_CANCELED: {
          yield createKnmReasonCanceled(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_DELEGATE_TYPES: {
          yield createKnmDelegateType(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_EXECUTION_TYPES_ALL: {
          yield createKnmForm(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_INSPECTOR_TYPES: {
          yield createKnmInspectorType(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_NOTICE_METHODS_ALL: {
          yield createKnmNoticeMethod(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_SUBJECT_TYPES_ALL: {
          yield createKnmSubjectType(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_TYPES_ALL: {
          yield createKnmType(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_VIOLATION_TYPES_ALL: {
          yield createKnmViolationType(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.RISK_CATEGORIES: {
          yield createRiskCategory(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.TEST_PURCHASE_METHODS: {
          yield createTestPurchaseMethod(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_REMARKS_ALL: {
          yield createKnmRemark(action.payload.params.data);
          break;
        }
        default:
      }
      yield delay(GET_CATALOGS_DELAY);
      yield put(adminGetCatalogTypes(action.payload.params.catalogName));
    },
  });
}

function* adminUpdateCatalogTypesSaga(action: ActionType<typeof adminUpdateCatalogTypes>) {
  yield sagaWrapper({
    *funcTry() {
      switch (action.payload.params.catalogName) {
        case CommonCatalogsNamesEnum.KNM_ADDRESS_TYPES: {
          yield updateKnmAddressType(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_REASONS_CANCELED: {
          yield updateKnmReasonsCanceled(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_DELEGATE_TYPES: {
          yield updateKnmDelegateType(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_EXECUTION_TYPES_ALL: {
          yield updateKnmForm(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_INSPECTOR_TYPES: {
          yield updateKnmInspectorType(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_NOTICE_METHODS_ALL: {
          yield updateKnmNoticeMethod(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_SUBJECT_TYPES_ALL: {
          yield updateKnmSubjectType(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_TYPES_ALL: {
          yield updateKnmType(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_KINDS_ALL: {
          yield updateKnmKind(action.payload.params.data);
          if (action.payload.params.callback) {
            action.payload.params.callback();
          }
          break;
        }
        case CommonCatalogsNamesEnum.KNM_VIOLATION_TYPES_ALL: {
          yield updateKnmViolationType(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.RISK_CATEGORIES: {
          yield updateRiskCategory(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.TEST_PURCHASE_METHODS: {
          yield updateTestPurchaseMethod(action.payload.params.data);
          break;
        }
        case CommonCatalogsNamesEnum.KNM_REMARKS_ALL: {
          yield updateKnmRemark(action.payload.params.data);
          break;
        }
        default:
      }
      yield delay(GET_CATALOGS_DELAY);
      yield put(adminGetCatalogTypes(action.payload.params.catalogName));
    },
  });
}

function* adminUpdateFrguSaga() {
  yield sagaWrapper({
    *funcTry() {
      yield updateFrguSync();
    },
  });
}

function* adminGetNewsSaga() {
  function* funcTry() {
    const sort: string = yield select(getAdminNewsOrder);
    const data: INewsResponse = yield fetchNews(sort, 0);

    yield put(adminSetNews(data.list, data.page, data.pageCount, data.totalCount));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* adminGetNewsNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const sort = getAdminNewsOrder(state);
    const page = getAdminNewsPage(state);
    const news = getAdminNews(state);
    const data: INewsResponse = yield fetchNews(sort, page + 1);

    yield put(adminSetNews([...news, ...data.list], data.page, data.pageCount, data.totalCount));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* adminCatalogsExportsSubmitSaga(action: ActionType<typeof adminCatalogsExportsSubmit>) {
  const { data, settings } = action.payload;

  const KNM_PLAN_MATCH_OUTPUT_DATE = settings.find(
    setting => setting.code === AdminSettingCodesEnum.KNM_PLAN_MATCH_OUTPUT_DATE && !setting.periods,
  );
  const KNM_MATCH_EXPORT_DATE = settings.find(
    setting => setting.code === AdminSettingCodesEnum.KNM_MATCH_EXPORT_DATE && !setting.periods,
  );
  const EXPORT_DOCUMENTS_KNO_DATE = settings.find(
    setting => setting.code === AdminSettingCodesEnum.EXPORT_DOCUMENTS_KNO_DATE,
  );
  const IMPORT_PLANS_DATE = settings.find(setting => setting.code === AdminSettingCodesEnum.IMPORT_PLANS_DATE);

  const KNM_PLAN_MATCH_OUTPUT_DATE_PERIOD = settings.find(
    setting => setting.code === AdminSettingCodesEnum.KNM_PLAN_MATCH_OUTPUT_DATE && setting.periods,
  );
  const KNM_MATCH_EXPORT_DATE_PERIOD = settings.find(
    setting => setting.code === AdminSettingCodesEnum.KNM_MATCH_EXPORT_DATE && setting.periods,
  );

  if (KNM_PLAN_MATCH_OUTPUT_DATE && data.KNM_PLAN_MATCH_OUTPUT_DATE) {
    yield put(
      adminUpdateSettings(
        {
          ...KNM_PLAN_MATCH_OUTPUT_DATE,
          value: formatDate({ date: data.KNM_PLAN_MATCH_OUTPUT_DATE, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
        },
        KNM_PLAN_MATCH_OUTPUT_DATE.id,
      ),
    );
  }

  if (KNM_MATCH_EXPORT_DATE && data.KNM_MATCH_EXPORT_DATE) {
    yield put(
      adminUpdateSettings(
        {
          ...KNM_MATCH_EXPORT_DATE,
          value: formatDate({ date: data.KNM_MATCH_EXPORT_DATE, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
        },
        KNM_MATCH_EXPORT_DATE.id,
      ),
    );
  }

  if (EXPORT_DOCUMENTS_KNO_DATE && data.EXPORT_DOCUMENTS_KNO_DATE) {
    yield put(
      adminUpdateSettings(
        {
          ...EXPORT_DOCUMENTS_KNO_DATE,
          value: formatDate({ date: data.EXPORT_DOCUMENTS_KNO_DATE, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
        },
        EXPORT_DOCUMENTS_KNO_DATE.id,
      ),
    );
  }

  if (IMPORT_PLANS_DATE && data.IMPORT_PLANS_DATE) {
    yield put(
      adminUpdateSettings(
        {
          ...IMPORT_PLANS_DATE,
          value: formatDate({ date: data.IMPORT_PLANS_DATE, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
        },
        IMPORT_PLANS_DATE.id,
      ),
    );
  }

  if (
    KNM_PLAN_MATCH_OUTPUT_DATE_PERIOD &&
    KNM_MATCH_EXPORT_DATE_PERIOD &&
    data.KNM_PLAN_MATCH_OUTPUT_DATE_PERIOD &&
    data.KNM_MATCH_EXPORT_DATE_PERIOD
  ) {
    yield put(
      adminUpdateSettingsSchedule([
        {
          ...KNM_MATCH_EXPORT_DATE_PERIOD,
          periods: data.KNM_MATCH_EXPORT_DATE_PERIOD.filter(item => !item.deleteId).map(item => {
            return {
              startDate: parseDate({
                date: item.startDate,
                parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
              })?.toISOString() as string,
              endDate: parseDate({
                date: item.endDate,
                parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
              })?.toISOString() as string,
            };
          }),
        },
        {
          ...KNM_PLAN_MATCH_OUTPUT_DATE_PERIOD,
          periods: data.KNM_PLAN_MATCH_OUTPUT_DATE_PERIOD.filter(item => !item.deleteId).map(item => {
            return {
              startDate: parseDate({
                date: item.startDate,
                parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
              })?.toISOString() as string,
              endDate: parseDate({
                date: item.endDate,
                parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
              })?.toISOString() as string,
            };
          }),
        },
      ]),
    );
  }

  yield put(adminGetFetchSettings(true));
  yield put(
    addNoticeAction({
      noticeType: NoticeTypesEnum.SUCCESS,
      messageType: MessageTypesEnum.ADMIN_SETTINGS_UPDATE,
      renderContent: 'Изменения успешно сохранены',
    }),
  );
}

function* adminGetSystemInoperabilityListSaga() {
  const state: IRootState = yield select();
  const sort = getAdminSystemInoperabilityOrder(state);

  try {
    yield put(coreSetLoading(true));
    const data: ISystemInoperabilityResponse = yield fetchSystemInoperabilityList(sort, 0);

    yield put(adminSetSystemInoperabilityList(data.list, data.page, data.pageCount, data.totalCount));
  } finally {
    yield put(coreSetLoading(false));
  }
}

function* adminGetSystemInoperabilityListNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const sort = getAdminSystemInoperabilityOrder(state);
    const page = getAdminSystemInoperabilityListPage(state);
    const list = getAdminSystemInoperabilityList(state);

    const data: ISystemInoperabilityResponse = yield fetchSystemInoperabilityList(sort, page + 1);

    yield put(adminSetSystemInoperabilityList([...list, ...data.list], data.page, data.pageCount, data.totalCount));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* adminSetSystemInoperabilityRecord(action: ActionType<typeof adminSetSystemInoperabilityAddRecord>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const sort = getAdminSystemInoperabilityOrder(state);
    const formData: ISystemInoperability[] = action.payload;

    // Вызвать апи на добавление записи, получить список не сохранившихся записей
    const unsavedRecords: ISystemInoperability[] = yield addSystemInoperabilityRecord(formData);
    // Запросить обновленный список записей
    const data: ISystemInoperabilityResponse = yield fetchSystemInoperabilityList(sort, 0);

    yield put(adminSetSystemInoperabilityList(data.list, data.page, data.pageCount, data.totalCount));
    if (unsavedRecords && unsavedRecords.length > 0) {
      yield put(adminSetSystemInoperabilityFormUnsavedRecords(unsavedRecords));
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      const errorMessage = error.message;

      if (errorMessage?.includes(SYSTEM_INOPERABILITY_NOT_ALL_SAVED)) {
        yield put(
          addNoticeAction({
            renderContent: SYSTEM_INOPERABILITY_ERROR_MESSAGE,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADMIN_ADD_SYSTEM_INOPERABILITY_RECORD,
          }),
        );
      } else {
        // Ошибка иного характера, без описания
        yield put(
          addNoticeAction({
            renderContent: SYSTEM_INOPERABILITY_ERROR_MESSAGE,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADMIN_ADD_SYSTEM_INOPERABILITY_RECORD,
          }),
        );
      }
    },
  });
}

function* adminGetSmevResendBySendingDateListSaga(action: ActionType<typeof adminGetSmevResendBySendingDate>) {
  const { startDate, endDate } = action.payload;

  try {
    yield put(coreSetLoading(true));
    const data: ISmevResendResponse = yield fetchSmevResendBySendingDateList(
      formatDate({ date: startDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
      formatDate({ date: endDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
      0,
    );

    yield put(adminSetSmevResendBySendingDateList(data.list, data.page, data.totalCount));
  } finally {
    yield put(coreSetLoading(false));
  }
}

function* adminGetSmevResendBySendingDateNextPageSaga(
  action: ActionType<typeof adminGetSmevResendBySendingDateNextPage>,
) {
  function* funcTry() {
    const { startDate, endDate } = action.payload;

    const state: IRootState = yield select();
    const page = getAdminControlOfNewslettersListPage(state);
    const list = getAdminControlOfNewslettersList(state);

    yield put(coreSetLoading(true));

    const data: ISmevResendResponse = yield fetchSmevResendBySendingDateList(
      formatDate({ date: startDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
      formatDate({ date: endDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
      page + 1,
    );

    yield put(adminSetSmevResendBySendingDateList([...list, ...data.list], data.page, data.totalCount));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* adminGetManageTestUsersListSaga(action: ActionType<typeof adminGetManageTestUsersList>) {
  const { username, fullName } = action.payload;
  const state: IRootState = yield select();
  const order = getAdminMangeTestUsersListOrder(state);

  try {
    yield put(coreSetLoading(true));
    const data: IManageTestUsersResponse = yield fetchManageTestUsersApiList({
      username,
      fullName,
      order,
      size: '10',
      page: '0',
    });

    yield put(adminSetManageTestUsersList(data.list, data.page, data.pageCount, data.totalCount));
  } finally {
    yield put(coreSetLoading(false));
  }
}

function* adminGetManageTestUsersListNextPageSaga(action: ActionType<typeof adminGetManageTestUsersListNextPage>) {
  const { username, fullName } = action.payload;

  function* funcTry() {
    const state: IRootState = yield select();
    const order = getAdminMangeTestUsersListOrder(state);
    const page = getAdminMangeTestUsersListPage(state);
    const list = getAdminMangeTestUsersList(state);

    const data: IManageTestUsersResponse = yield fetchManageTestUsersApiList({
      username,
      fullName,
      order,
      size: '50',
      page: list.length === 10 ? '0' : (page + 1).toString(),
      shift: '10',
    });

    yield put(adminSetManageTestUsersList([...list, ...data.list], data.page, data.pageCount, data.totalCount));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* adminUpdateManageTestUsersListSaga(action: ActionType<typeof adminUpdateManageTestUsersList>) {
  const { list, username, fullName } = action.payload;

  try {
    yield put(coreSetLoading(true));
    yield fetchUpdateManageTestUsersApiList(list);
    yield adminGetManageTestUsersListSaga({
      type: AdminActionsEnum.ADMIN_GET_MANAGE_TEST_USERS_LIST,
      payload: { username, fullName },
    });
    yield put(
      addNoticeAction({
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.ADMIN_SETTINGS_UPDATE,
        renderContent: 'Изменения успешно сохранены',
      }),
    );
  } finally {
    yield put(coreSetLoading(false));
  }
}
